import { Autocomplete, Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import debounce from 'lodash/debounce';
import { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { botelService, Location } from '../services/api/botelService';

// Constants moved to separate file
import { DURATIONS, PROMO_CARDS } from '../constants/booking';

// Memoized PromoCard component
const PromoCard = memo(({ card }: { card: typeof PROMO_CARDS[0] }) => (
  <Paper elevation={6} sx={{
    bgcolor: card.bgcolor,
    color: '#fff',
    borderRadius: 4,
    p: 3,
    height: '100%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
  }}>
    <Box sx={{ mb: 1 }}>
      <img src={card.icon} alt={card.title} width={40} height={40} style={{ filter: 'brightness(1.2)' }} />
    </Box>
    <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>{card.title}</Typography>
    <Typography sx={{ color: '#b0b8c1', fontSize: 15 }}>{card.subtitle}</Typography>
    <Button 
      variant="contained" 
      fullWidth 
      sx={{ 
        mt: 2, 
        bgcolor: card.color, 
        color: '#fff', 
        fontWeight: 700, 
        borderRadius: 2, 
        '&:hover': { bgcolor: card.color } 
      }}
    >
      {card.button}
    </Button>
  </Paper>
));

// Memoized BookingForm component
const BookingForm = memo(({ 
  location, 
  setLocation, 
  duration, 
  setDuration, 
  dateTime, 
  setDateTime, 
  insurance, 
  setInsurance,
  errors,
  handleSubmit
}: {
  location: string;
  setLocation: (value: string) => void;
  duration: string;
  setDuration: (value: string) => void;
  dateTime: string;
  setDateTime: (value: string) => void;
  insurance: boolean;
  setInsurance: (value: boolean) => void;
  errors: {
    location: string;
    duration: string;
    dateTime: string;
  };
  handleSubmit: () => void;
}) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalLocations, setTotalLocations] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchLocations = async (query?: string, pageNum: number = 1) => {
    try {
      setLoading(true);
      const response = await botelService.searchLocations(query, pageNum);
      if (pageNum === 1) {
        setLocations(response.locations);
      } else {
        setLocations(prev => [...prev, ...response.locations]);
      }
      setTotalLocations(response.total);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load initial locations
  useEffect(() => {
    fetchLocations();
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(async (query: string) => {
      if (query.length >= 2) {
        setPage(1);
        await fetchLocations(query, 1);
      } else if (query.length === 0) {
        await fetchLocations();
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const target = event.target as HTMLDivElement;
    if (
      !loading &&
      target.scrollTop + target.clientHeight >= target.scrollHeight - 20 &&
      locations.length < totalLocations
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchLocations(searchQuery, nextPage);
    }
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent) => {
    if (field === 'duration') setDuration(event.target.value);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDateTime(event.target.value);
  };

  return (
    <Box sx={{
      bgcolor: '#232933',
      borderRadius: 4,
      p: { xs: 2, md: 4 },
      maxWidth: 1200,
      mx: 'auto',
      boxShadow: 6,
      color: '#fff',
      position: 'relative',
    }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Book Your Botel Now
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid size={4}>
          <Typography variant="subtitle2" mb={1}>
            Location
          </Typography>
          <Autocomplete
            freeSolo
            options={locations}
            getOptionLabel={(option) => 
              typeof option === 'string' ? option : option.location
            }
            inputValue={searchQuery}
            onInputChange={(_, newValue) => {
              setSearchQuery(newValue);
            }}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                setLocation(newValue);
              } else if (newValue) {
                setLocation(newValue.location);
              }
            }}
            ListboxProps={{
              onScroll: handleScroll,
              style: { maxHeight: '200px' }
            }}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                error={!!errors.location}
                sx={{ 
                  bgcolor: '#1a1f24', 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: errors.location ? 'error.main' : 'rgba(255, 255, 255, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                  },
                }}
                placeholder="Search for a location"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body1">{option.location}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.location_address}
                  </Typography>
                </Box>
              </li>
            )}
          />
          {errors.location && (
            <Typography color="error" variant="caption">{errors.location}</Typography>
          )}
        </Grid>
        <Grid size={4}>
          <Typography variant="subtitle2" mb={1}>
            Duration
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={duration}
            onChange={handleSelectChange('duration')}
            error={!!errors.duration}
            sx={{ bgcolor: '#1a1f24', color: '#fff', borderRadius: 2 }}
            renderValue={selected => selected || 'Select Duration'}
          >
            <MenuItem disabled value="">
              Select Duration
            </MenuItem>
            {DURATIONS.map(d => (
              <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
            ))}
          </Select>
          {errors.duration && (
            <Typography color="error" variant="caption">{errors.duration}</Typography>
          )}
        </Grid>
        <Grid size={4}>
          <Typography variant="subtitle2" mb={1}>
            Date & Time
          </Typography>
          <TextField
            fullWidth
            type="datetime-local"
            value={dateTime}
            onChange={handleInputChange}
            error={!!errors.dateTime}
            sx={{ bgcolor: '#1a1f24', borderRadius: 2, input: { color: '#fff' } }}
            InputLabelProps={{ shrink: true }}
          />
          {errors.dateTime && (
            <Typography color="error" variant="caption">{errors.dateTime}</Typography>
          )}
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={insurance}
                onChange={e => setInsurance(e.target.checked)}
                sx={{ color: '#2563EB' }}
              />
            }
            label={<Typography color="#fff">Add Premium Insurance ($5) Protect Up to $10K</Typography>}
          />
        </Grid>
        <Grid size={12}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{ fontWeight: 700, borderRadius: 2, py: 1.5, fontSize: 18 }}
          >
            BOOK NOW & GET $50 FREE CREDIT
          </Button>
        </Grid>
      </Grid>
      <Box sx={{ position: 'absolute', top: 24, right: 32, bgcolor: '#232933', border: '2px solid #e53935', borderRadius: 2, px: 2, py: 0.5, color: '#e53935', fontWeight: 700, fontSize: 18 }}>
        <span role="img" aria-label="clock">⏰</span> 45:57:30
      </Box>
    </Box>
  );
});

export default function BookBotelSection() {
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [duration, setDuration] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [insurance, setInsurance] = useState(false);
  const [errors, setErrors] = useState({
    location: '',
    duration: '',
    dateTime: ''
  });

  const validateForm = () => {
    const newErrors = {
      location: '',
      duration: '',
      dateTime: ''
    };
    let isValid = true;

    if (!location) {
      newErrors.location = 'Location is required';
      isValid = false;
    }

    if (!duration) {
      newErrors.duration = 'Duration is required';
      isValid = false;
    }

    if (!dateTime) {
      newErrors.dateTime = 'Date and time is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Convert form data to URL parameters
      const params = new URLSearchParams({
        location,
        dateTime,
        duration,
        insurance: insurance.toString()
      });
      
      // Navigate to filtered botels page with parameters
      navigate(`/filtered-botels?${params.toString()}`);
    }
  };

  // Memoize the header content
  const headerContent = useMemo(() => (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box sx={{ bgcolor: '#2563EB', color: '#fff', borderRadius: 2, px: 2.5, py: 0.5, fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>
          COMPETITIVE ADVANTAGE
        </Box>
      </Box>
      <Typography variant="h4" fontWeight={700} align="center" sx={{ color: '#fff', mb: 1, fontSize: { xs: 24, md: 32 } }}>
        READY TO JOIN THE SMART STORAGE REVOLUTION?
      </Typography>
      <Typography align="center" sx={{ color: '#b0b8c1', mb: 4, fontSize: { xs: 14, md: 18 } }}>
        Botel is Changing The Storage Industry Forever — Be Part Of It.
      </Typography>
    </>
  ), []);

  return (
    <Box sx={{ width: '100%', py: { xs: 4, md: 8 }, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{
        width: '100%',
        bgcolor: 'transparent',
        pt: { xs: 4, md: 8 },
        maxWidth: 1200,
        mx: 'auto',
        pb: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Typography variant="h2" color="text.secondary">
          Find Your Perfect Storage Solution
        </Typography>
        <BookingForm
          location={location}
          setLocation={setLocation}
          duration={duration}
          setDuration={setDuration}
          dateTime={dateTime}
          setDateTime={setDateTime}
          insurance={insurance}
          setInsurance={setInsurance}
          errors={errors}
          handleSubmit={handleSubmit}
        />
      </Box>
    </Box>
  );
} 